import { useState, useCallback, useEffect } from 'react';
import type { TableState } from './table-state.js';

export interface SavedView {
  id: string;
  name: string;
  isDefault?: boolean;
  isShared?: boolean;
  schemaVersion?: number;
  state?: Partial<TableState>;
}

interface UseSavedViewsOptions {
  tableId: string;
  initialViews?: SavedView[];
  schemaVersion?: number;
}

interface UseSavedViewsReturn {
  views: SavedView[];
  currentViewId: string | null;
  selectView: (id: string) => SavedView | null;
  saveView: (name: string, state?: Partial<TableState>) => SavedView;
  deleteView: (id: string) => void;
}

const STORAGE_KEY = 'finance:saved-views';

function readViews(tableId: string): SavedView[] {
  if (typeof window === 'undefined' || !window.localStorage) return [];
  try {
    const raw = window.localStorage.getItem(`${STORAGE_KEY}:${tableId}`);
    return raw ? (JSON.parse(raw) as SavedView[]) : [];
  } catch {
    return [];
  }
}

function writeViews(tableId: string, views: SavedView[]): void {
  if (typeof window === 'undefined' || !window.localStorage) return;
  try {
    window.localStorage.setItem(`${STORAGE_KEY}:${tableId}`, JSON.stringify(views));
  } catch {
    // Ignore storage errors (e.g. quota exceeded)
  }
}

const EMPTY_VIEWS: SavedView[] = [];

export function useSavedViews({ tableId, initialViews = EMPTY_VIEWS, schemaVersion }: UseSavedViewsOptions): UseSavedViewsReturn {
  const [views, setViews] = useState<SavedView[]>(initialViews);
  const [currentViewId, setCurrentViewId] = useState<string | null>(null);

  useEffect(() => {
    const stored = readViews(tableId);
    const merged = stored.length > 0 ? stored : initialViews;
    const normalized = schemaVersion == null
      ? merged
      : merged.filter((view) => view.schemaVersion == null || view.schemaVersion === schemaVersion);
    setViews(normalized);
    const defaultView = normalized.find((view) => view.isDefault);
    setCurrentViewId(defaultView?.id ?? null);
  }, [tableId, initialViews, schemaVersion]);

  useEffect(() => {
    writeViews(tableId, views);
  }, [tableId, views]);

  const selectView = useCallback((id: string) => {
    const selectedView = views.find((view) => view.id === id) ?? null;
    setCurrentViewId(id);
    return selectedView;
  }, [views]);

  const saveView = useCallback((name: string, state?: Partial<TableState>) => {
    const newView: SavedView = { id: `local-${Date.now()}`, name, schemaVersion, state };
    setViews((prev) => [...prev, newView]);
    setCurrentViewId(newView.id);
    return newView;
  }, [schemaVersion]);

  const deleteView = useCallback((id: string) => {
    setViews((prev) => prev.filter((v) => v.id !== id));
    if (currentViewId === id) setCurrentViewId(null);
  }, [currentViewId]);

  return { views, currentViewId, selectView, saveView, deleteView };
}
